const SITE_URL = 'https://buildingontruth.com';
const ORG = {
  '@type': 'Organization',
  name: 'Building on Truth',
  url: SITE_URL,
  logo: `${SITE_URL}/img/logo_2025_mvp.jpg`,
};

const PERSON_ANDREW = {
  '@type': 'Person',
  name: 'Andrew Schwabe',
  url: `${SITE_URL}/about`,
  image: `${SITE_URL}/img/andrew_pic.JPG`,
  jobTitle: 'Co-Founder, AI Researcher, Serial Entrepreneur',
  worksFor: ORG,
  sameAs: ['https://www.linkedin.com/in/aschwabe/'],
};

const PERSON_REBECCA = {
  '@type': 'Person',
  name: 'Rebecca Schwabe',
  alternateName: 'Rebecca Joy',
  url: `${SITE_URL}/about`,
  image: `${SITE_URL}/img/rebecca_pic.png`,
  jobTitle: 'Co-Founder, Data Scientist, CEO of Saigon A.I.',
  worksFor: ORG,
  sameAs: ['https://www.linkedin.com/in/rebecca-schwabe/'],
};

export const PEOPLE = { andrew: PERSON_ANDREW, rebecca: PERSON_REBECCA };

export const homeSchemas = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Building on Truth | Where Faith Meets Innovation',
    url: SITE_URL,
    description: 'Equipping Christians to engage AI, entrepreneurship, and business as a mission with biblical faithfulness and kingdom purpose.',
    inLanguage: 'en-US',
    isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'Building on Truth' },
    primaryImageOfPage: { '@type': 'ImageObject', url: `${SITE_URL}/img/og-default.jpg` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', 'blockquote'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Building on Truth?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Building on Truth is a teaching ministry founded by Andrew and Rebecca Schwabe that equips Christians to engage AI, entrepreneurship, and business as a mission with biblical faithfulness. We offer workshops, speaking engagements, ministry incubators, and books for churches, conferences, universities, and organizations.',
        },
      },
      {
        '@type': 'Question',
        name: 'Who are the founders of Building on Truth?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Andrew Schwabe is a serial entrepreneur and AI researcher at the University of St. Andrews with 30+ years building technology companies. Rebecca Schwabe (Rebecca Joy) is a UC Berkeley-educated data scientist and CEO of Saigon A.I., serving Global Fortune 100 clients.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is Business as a Mission (BAM)?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Business as a Mission is the conviction that a Christian-run company can be a primary platform for gospel witness and kingdom impact — not secondary to ministry. It embeds gospel purpose into culture, hiring, products, and community relationships.',
        },
      },
      {
        '@type': 'Question',
        name: 'How should Christians think about artificial intelligence?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Christians should engage AI with theological depth, ethical clarity, and practical wisdom. AI is a powerful tool that reflects the hearts of those who build and use it. Our workshops cover the image of God, human dignity, privacy, bias, and responsible AI use in work and ministry.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I book Andrew and Rebecca to speak at my event?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Andrew and Rebecca speak at churches, conferences, universities, and organizations worldwide on AI, faith, entrepreneurship, and business as a mission. Contact us via buildingontruth.com/contact.',
        },
      },
    ],
  },
];

export const workshopsSchemas = () => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Workshops by Building on Truth',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Course',
        name: 'AI & The Christian Life',
        description: 'Theological foundations, ethical frameworks, and practical wisdom for engaging artificial intelligence as a Christian.',
        provider: ORG,
        url: `${SITE_URL}/workshops`,
        hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'onsite' },
        offers: { '@type': 'Offer', category: 'Workshop', availability: 'https://schema.org/InStock', url: `${SITE_URL}/contact` },
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Course',
        name: 'Entrepreneurship & Biblical Calling',
        description: 'Understanding entrepreneurship as a legitimate Christian calling and building businesses on biblical principles.',
        provider: ORG,
        url: `${SITE_URL}/workshops`,
        hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'onsite' },
        offers: { '@type': 'Offer', category: 'Workshop', availability: 'https://schema.org/InStock', url: `${SITE_URL}/contact` },
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Course',
        name: 'Business as a Mission',
        description: 'Biblical foundations and real-world models for integrating gospel purpose into company culture, hiring, products, and community.',
        provider: ORG,
        url: `${SITE_URL}/workshops`,
        hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'onsite' },
        offers: { '@type': 'Offer', category: 'Workshop', availability: 'https://schema.org/InStock', url: `${SITE_URL}/contact` },
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Course',
        name: 'Premarital Counseling',
        description: 'Biblical premarital counseling helping couples build marriages on honesty, grace, and covenant.',
        provider: ORG,
        url: `${SITE_URL}/workshops`,
        hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'onsite' },
        offers: { '@type': 'Offer', category: 'Workshop', availability: 'https://schema.org/InStock', url: `${SITE_URL}/contact` },
      },
    },
  ],
});

const MONTHS = {
  January: '01', February: '02', March: '03', April: '04', May: '05', June: '06',
  July: '07', August: '08', September: '09', October: '10', November: '11', December: '12',
};

export const parseDisplayDate = (display) => {
  if (!display) return undefined;
  const m = display.match(/([A-Z][a-z]+)\s+(\d{1,2})(?:[–-]\d{1,2})?,\s+(\d{4})/);
  if (!m) return undefined;
  const month = MONTHS[m[1]];
  if (!month) return undefined;
  return `${m[3]}-${month}-${m[2].padStart(2, '0')}`;
};

export const eventsSchemas = (pastEvents) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Past Speaking Engagements',
  itemListElement: pastEvents.map((e, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Event',
      name: e.title,
      eventStatus: 'https://schema.org/EventCompleted',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      startDate: parseDisplayDate(e.date),
      location: { '@type': 'Place', name: `${e.host} — ${e.location}`, address: e.location },
      organizer: { '@type': 'Organization', name: e.host },
      performer: [PERSON_ANDREW, PERSON_REBECCA],
    },
  })),
});

export const resourcesSchemas = (books) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Books by Building on Truth',
  itemListElement: books.map((b, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Book',
      name: b.title,
      author: b.author.includes('&')
        ? b.author.split('&').map((n) => ({ '@type': 'Person', name: n.trim() }))
        : { '@type': 'Person', name: b.author },
      bookFormat: 'https://schema.org/EBook',
      image: `${SITE_URL}${b.cover}`,
      description: b.description,
      inLanguage: 'en',
      publisher: ORG,
      ...(b.status === 'available'
        ? { offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', url: `${SITE_URL}/contact` } }
        : { workExample: { '@type': 'Book', bookEdition: b.statusLabel } }),
    },
  })),
});

export const aboutSchemas = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Building on Truth',
    url: `${SITE_URL}/about`,
    description: 'Meet Andrew Schwabe and Rebecca Joy — the entrepreneurs, researchers, and teachers behind Building on Truth.',
    mainEntity: ORG,
  },
  { '@context': 'https://schema.org', ...PERSON_ANDREW },
  { '@context': 'https://schema.org', ...PERSON_REBECCA },
];

export const incubatorsSchemas = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Ministry Incubators',
  serviceType: 'Business as a Mission (BAM) Incubator',
  provider: ORG,
  areaServed: 'Worldwide',
  description: 'Helping business owners and entrepreneurs integrate Business as a Mission — discovering calling, building on biblical foundations, embedding gospel purpose into company culture, and growing with accountability.',
  audience: [
    { '@type': 'Audience', audienceType: 'Established Business Owners' },
    { '@type': 'Audience', audienceType: 'Entrepreneurs' },
    { '@type': 'Audience', audienceType: 'Marketplace Professionals' },
    { '@type': 'Audience', audienceType: 'Church and Ministry Leaders' },
  ],
  url: `${SITE_URL}/ministry-incubators`,
});

export const contactSchemas = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Building on Truth',
  url: `${SITE_URL}/contact`,
  description: 'Contact Building on Truth for books, speaking engagements, workshops, and training inquiries.',
  mainEntity: {
    ...ORG,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'general inquiries',
      url: `${SITE_URL}/contact`,
      areaServed: 'Worldwide',
      availableLanguage: ['English'],
    },
  },
});
